import React, { FC, ReactNode, isValidElement } from 'react'
import { Card, ICardProps } from 'transfix-ui/components/Card'
import { Box, Grid, makeStyles, Skeleton } from 'transfix-ui/core'
import { NoDataGraphic } from 'apps/tms/components/NoDataGraphic'
import { InsightsCardChartBlock } from './InsightsCardChartBlock'
import { InsightsCardKeyMetric } from './InsightsCardKeyMetric'
import { InsightsCardSubtitle } from './InsightsCardSubtitle'
import { InsightsCardTitle } from './InsightsCardTitle'

export interface IInsightsCardProps extends ICardProps {
  /**
   * @default 'left'
   */
  alignment?: 'left' | 'centered'
  badge?: ReactNode
  description?: string
  /**
   * @default false
   */
  displayNoDataAvailable?: boolean
  /**
   * @default true
   */
  isLoading?: boolean
  keyMetric?: ReactNode
  timeRange?: string | ReactNode
  title: string
  withContentPadding?: boolean
}

const CARD_PADDING_PX = 3
const INITIAL_CARD_HEIGHT = 260

const useClasses = makeStyles({
  cardContainer: {
    minHeight: INITIAL_CARD_HEIGHT,
    '& .tooltip': {
      // This number is coming from the class .med-tooltip-width that's defined in the tms/main.scss file
      maxWidth: 340,
    },
    '& .card-content': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  cardContentCentered: {
    '& .card-content': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
})

export const InsightsCard: FC<IInsightsCardProps> = props => {
  const {
    alignment = 'left',
    badge,
    children,
    description,
    displayNoDataAvailable = false,
    isLoading = true,
    keyMetric,
    timeRange,
    title,
    withContentPadding,
    ...cardProps
  } = props

  const isLeftAligned = alignment === 'left'
  const classes = useClasses()

  return (
    <Card
      noPadding
      fillContainer
      className={`${!isLeftAligned ? classes.cardContentCentered : ''} ${classes.cardContainer}`}
      {...cardProps}>
      <Box p={CARD_PADDING_PX} display='flex' justifyContent={isLeftAligned ? 'left' : 'center'}>
        <Grid
          container
          justifyContent='space-between'
          direction={isLeftAligned ? 'row' : 'column'}
          alignItems={isLeftAligned ? 'flex-start' : 'center'}>
          <Box
            display='flex'
            flexDirection='column'
            alignItems={isLeftAligned ? 'flex-start' : 'center'}>
            <InsightsCardTitle
              text={title}
              isLoading={isLoading}
              tooltip={description}
              spacing={!isLeftAligned}
            />
            <InsightsCardKeyMetric value={keyMetric} isLoading={isLoading} />
            <InsightsCardSubtitle
              isLoading={isLoading}
              text={timeRange}
              withBottomSpacing={!isLeftAligned && badge !== undefined}
              withTopSpacing={isLeftAligned && badge === undefined}
            />
          </Box>

          {badge && isLoading && <Skeleton width={50} animation='wave' />}
          {badge && !isLoading && badge}
        </Grid>
      </Box>

      {!displayNoDataAvailable && isValidElement(children) && (
        <InsightsCardChartBlock withContentPadding={withContentPadding} isLoading={isLoading}>
          {children}
        </InsightsCardChartBlock>
      )}
      {displayNoDataAvailable && !isLoading && <NoDataGraphic />}
    </Card>
  )
}
